import { useState, useEffect } from 'react'
import { Button } from './ui/button'
import { X } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { compareDivisions } from '../lib/divisionOrder'
import {
  fetchDivisionsBySport,
  createBracket,
} from '../services/tournamentService'

export default function AddBracketModal({
  isOpen,
  onClose,
  tournamentId,
  sportId,
  existingDivisionIds = [],
  hasPoolPlayBracket = false,
  onBracketCreated,
}) {
  const [divisions, setDivisions] = useState([])
  const [selectedDivision, setSelectedDivision] = useState('')
  const [selectedFormat, setSelectedFormat] = useState('single_elim')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const isPoolPlay = selectedFormat === 'pool_then_sunday'

  useEffect(() => {
    if (isOpen && sportId) {
      fetchDivisions()
    }
  }, [isOpen, sportId, existingDivisionIds.join(',')])

  const fetchDivisions = async () => {
    try {
      const data = await fetchDivisionsBySport({ sportId })
      const usedIds = new Set(existingDivisionIds)
      const available = (data || [])
        .filter(d => !usedIds.has(d.id))
        .sort(compareDivisions)
      setDivisions(available)
      setSelectedDivision(available.length > 0 ? available[0].id : '')
    } catch (err) {
      console.error('Error fetching divisions:', err)
      setError('Failed to load divisions')
    }
  }

  const handleCreate = async () => {
    // Pool Play brackets are decoupled from divisions (DUB-127). For
    // single-elim a division is still required.
    if (!isPoolPlay && !selectedDivision) {
      setError('Please select a division')
      return
    }
    if (isPoolPlay && hasPoolPlayBracket) {
      setError('This tournament already has a Pool Play bracket. Only one is allowed.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await createBracket({
        tournamentId,
        divisionId: isPoolPlay ? null : selectedDivision,
        format: selectedFormat,
      })

      onBracketCreated(data)
      onClose()
    } catch (err) {
      console.error('Error creating bracket:', err)
      const msg = err?.message || ''
      const isDuplicateDivision = err?.code === '23505' || /brackets_tournament_division_unique/i.test(msg)
      const isDuplicatePoolPlay = /brackets_one_pool_play_per_tournament/i.test(msg)
      if (isDuplicatePoolPlay) {
        setError('This tournament already has a Pool Play bracket. Only one is allowed.')
      } else if (isDuplicateDivision) {
        setError('A bracket for this division already exists in this tournament.')
      } else {
        setError(msg || 'Failed to create bracket')
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  // Disable the Create button when the current selection can't possibly succeed.
  const createDisabled =
    loading ||
    (isPoolPlay ? hasPoolPlayBracket : (!selectedDivision || divisions.length === 0))

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Add Bracket</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
              {error}
            </div>
          )}

          {/* Format first — division is only relevant to Single Elimination. */}
          <div>
            <label className="block text-sm font-medium mb-2">Format</label>
            <Select value={selectedFormat} onValueChange={setSelectedFormat}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single_elim">Single Elimination</SelectItem>
                <SelectItem value="pool_then_sunday">Pool Play</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {isPoolPlay
                ? 'Pool Play: two pool round-robins, then four cross-pool Finals matches. Teams are entered by name (no divisions).'
                : 'Single Elimination: standard knockout bracket within a division.'}
            </p>
          </div>

          {!isPoolPlay && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Division
              </label>
              <Select
                value={selectedDivision || undefined}
                onValueChange={setSelectedDivision}
                disabled={divisions.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={divisions.length === 0 ? (existingDivisionIds.length > 0 ? "All divisions already have brackets" : "No divisions available") : "Select division"} />
                </SelectTrigger>
                <SelectContent>
                  {divisions.map((division) => (
                    <SelectItem key={division.id} value={division.id}>
                      {division.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isPoolPlay && hasPoolPlayBracket && (
            <div className="bg-muted/40 text-muted-foreground text-sm p-3 rounded-md border">
              This tournament already has a Pool Play bracket. Only one is allowed.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={createDisabled}
          >
            {loading ? 'Creating...' : 'Create Bracket'}
          </Button>
        </div>
      </div>
    </div>
  )
}
